/**
 * 投稿モデレーションの AI 検証集計ヘルパ + インラインサマリ component (Admin Wave AW5、docs/108)。
 *
 * old2 `apps/admin/src/pages/PostsModerationPage.tsx` の以下を逐語移植:
 *   - `aggregateVerificationCounts` (L1097-1126): verificationSummary の perPost / perClassification
 *     配下の全 tally を walk して 妥当 (valid+yes) / どちらでもない (neutral) / 妥当ではない
 *     (invalid+no) の票数に集約。VerificationResultsPage と同等のロジック。
 *   - `VerificationInlineSummary` (L1132-1173): 票数 + 妥当/中立/不当 の数値 + 横棒比率バー。
 *     回答 0 件 / total 0 のときは何も描画しない (既存レイアウトに干渉しない)。
 *
 * old2 との差分:
 *   - 配色を v2 preset の success/neutral/danger tone に統一 (旧の green/red/neutral 直書きを置換)。
 *   - 比率バーの width は data 駆動 (動的 %) のため style={{ width }} を使用
 *     (VerificationResultsPage / SurveyResponsesPage と同方針、design token の inline 直書きではない)。
 */
import type { AiVerificationSummary, AiVerificationTally } from '@citizen-walk/shared-types';

export interface VerificationCounts {
  responseCount: number;
  validCount: number;
  neutralCount: number;
  invalidCount: number;
}

/**
 * verificationSummary の perPost / perClassification 配下の全 tally を walk して
 * 妥当 (valid + yes) / どちらでもない (neutral) / 妥当ではない (invalid + no) に集約する。
 * old2 `aggregateVerificationCounts` の逐語移植。
 */
export function aggregateVerificationCounts(
  summary: AiVerificationSummary | null | undefined,
): VerificationCounts {
  if (!summary) {
    return { responseCount: 0, validCount: 0, neutralCount: 0, invalidCount: 0 };
  }
  let valid = 0;
  let neutral = 0;
  let invalid = 0;
  const walkTally = (tally: AiVerificationTally) => {
    valid += tally.valid ?? 0;
    neutral += tally.neutral ?? 0;
    invalid += tally.invalid ?? 0;
    if (tally.yes !== undefined) valid += tally.yes;
    if (tally.no !== undefined) invalid += tally.no;
  };
  for (const tally of Object.values(summary.perPost ?? {})) walkTally(tally);
  for (const inner of Object.values(summary.perClassification ?? {})) {
    for (const tally of Object.values(inner)) walkTally(tally);
  }
  return {
    responseCount: summary.completedResponses ?? 0,
    validCount: valid,
    neutralCount: neutral,
    invalidCount: invalid,
  };
}

/**
 * 投稿カード / 詳細 modal 内に表示する検証集計のインライン要約。
 * 回答 0 件 / total 0 の投稿には何も描画しない (old2 と同挙動)。
 */
export function VerificationInlineSummary({
  summary,
}: {
  summary: AiVerificationSummary | null | undefined;
}) {
  if (!summary || (summary.completedResponses ?? 0) === 0) return null;
  const { responseCount, validCount, neutralCount, invalidCount } =
    aggregateVerificationCounts(summary);
  const total = validCount + neutralCount + invalidCount;
  if (total === 0) return null;
  const validPct = (validCount / total) * 100;
  const neutralPct = (neutralCount / total) * 100;
  const invalidPct = (invalidCount / total) * 100;
  return (
    <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
      <span className="text-slate-500">✓ 検証:</span>
      <span className="font-semibold">{responseCount} 票</span>
      <span className="text-slate-400">·</span>
      <span className="text-success-700 font-semibold">妥当 {validCount}</span>
      <span className="text-slate-400">/</span>
      <span className="text-slate-600">どちらでもない {neutralCount}</span>
      <span className="text-slate-400">/</span>
      <span className="text-danger-700 font-semibold">妥当ではない {invalidCount}</span>
      <div className="flex h-2 w-[120px] rounded overflow-hidden border border-slate-200">
        <div
          className="bg-success-600"
          style={{ width: `${validPct}%` }}
          title={`妥当 ${validPct.toFixed(0)}%`}
        />
        <div
          className="bg-slate-400"
          style={{ width: `${neutralPct}%` }}
          title={`どちらでもない ${neutralPct.toFixed(0)}%`}
        />
        <div
          className="bg-danger-600"
          style={{ width: `${invalidPct}%` }}
          title={`妥当ではない ${invalidPct.toFixed(0)}%`}
        />
      </div>
    </div>
  );
}
